import { merge } from 'lodash';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useId } from 'react';
import { IconType } from 'react-icons';
import { HiOutlineChevronDown, HiOutlineX, HiX } from 'react-icons/hi';
import { HiOutlineHeart } from 'react-icons/hi2';
import Select, {
   CSSObjectWithLabel,
   ClearIndicatorProps,
   DropdownIndicatorProps,
   MultiValueRemoveProps,
   StylesConfig,
   components,
   ControlProps,
   SelectInstance,
} from 'react-select';
import { FormFieldProps } from 'src/components/admin/form/types';
import ShowMoreMultipleItemsModal from 'src/components/admin/modal/ShowMoreMultipleItemsModal';
import MaxRowsValueContainer from 'src/components/admin/form/MaxRowsValueContainer';
import { colorDefault } from 'src/utils/constants';
import { twMerge } from 'tailwind-merge';

type SelectOption = {
   label: string;
   value: string;
   HtmlOptionLabel?: React.FC;
   HtmlInputLabel?: React.FC;
};

const ClearIndicator = (props: ClearIndicatorProps) => {
   return (
      <components.ClearIndicator {...props}>
         <HiX size={10} className="select-clear-indicator" />
      </components.ClearIndicator>
   );
};

const DropdownIndicator = (props: DropdownIndicatorProps) => {
   return (
      <components.DropdownIndicator {...props}>
         <HiOutlineChevronDown size={18} color="black" />
      </components.DropdownIndicator>
   );
};

const MultiValueRemove = (props: MultiValueRemoveProps) => {
   return (
      <components.MultiValueRemove {...props}>
         <HiOutlineX size={10} className="select-clear-indicator" />
      </components.MultiValueRemove>
   );
};

const Control = (props: ControlProps) => {
   return (
      <components.Control {...props}>
         <div className="pl-2">
            <HiOutlineHeart size={24} color="#686868" />
         </div>
         {props.children}
      </components.Control>
   );
};

const Option = (props: any) => {
   const { OptionStartIcon, OptionEndIcon, optionIconWrapper, label } = props;
   const { HtmlOptionLabel } = props.data;

   return (
      <components.Option {...props}>
         <div
            className={twMerge(
               'max-h-12.5 w-full flex items-center gap-2 justify-between',
               OptionEndIcon ? 'justify-between' : 'justify-start',
               HtmlOptionLabel ? 'border border-border p-2.5 rounded-6' : undefined,
            )}
         >
            {OptionStartIcon && (
               <div
                  className={twMerge(
                     `min-w-5 min-h-5 border rounded flex items-center justify-center}`,
                     props?.isSelected ? `bg-primary` : 'bg-white',
                     optionIconWrapper,
                  )}
               >
                  {props.isSelected && <OptionStartIcon />}
               </div>
            )}

            {HtmlOptionLabel ? (
               <HtmlOptionLabel />
            ) : (
               <span className="truncate whitespace-nowrap" title={label}>
                  {label}
               </span>
            )}

            {OptionEndIcon && (
               <div
                  className={twMerge(
                     `min-w-5 min-h-5 border rounded flex items-center justify-center}`,
                     props?.isSelected ? `bg-primary` : 'bg-white',
                     optionIconWrapper,
                  )}
               >
                  {props.isSelected && <OptionEndIcon />}
               </div>
            )}
         </div>
      </components.Option>
   );
};

const MenuList = (props: any) => {
   const childArray = React.Children.toArray(props.children);
   const firstChild = childArray[0] as any;
   const isFirstSelectAll = firstChild?.props?.data?.value === '__select_all__';
   const isNoOptions = firstChild?.props?.children === 'No options';
   const selectAllChild = isFirstSelectAll ? childArray[0] : null;
   const restChildren = isFirstSelectAll ? childArray.slice(1) : childArray;

   return (
      <components.MenuList {...props}>
         {selectAllChild && (
            <div className="px-3.75 py-2 border-b border-border font-semibold text-sm text-primary cursor-pointer hover:bg-lightGray">
               {selectAllChild}
            </div>
         )}
         {isNoOptions ? (
            <div className="px-3.75 py-2.5 text-sm text-twGray">{restChildren}</div>
         ) : (
            <div
               className={
                  props?.menuListClassName ??
                  'grid grid-cols-1 sm:grid-cols-3 max-h-[300px] gap-3.75 p-3.75 [&>[role=option]]:p-0 [&>[role=option]]:rounded-md [&>[role=option]]:w-full'
               }
            >
               {restChildren}
            </div>
         )}
      </components.MenuList>
   );
};

const SingleValue = (props: any) => (
   <components.SingleValue {...props}>
      <div className="truncate whitespace-nowrap leading-6" title={props.children}>
         {props.children}
      </div>
   </components.SingleValue>
);

const MultiValue = (props: any) => {
   const { HtmlInputLabel } = props.data;

   return (
      <components.MultiValue {...props}>
         <div className={'flex items-center gap-2 justify-between'}>
            {HtmlInputLabel ? (
               <HtmlInputLabel />
            ) : (
               <span className="truncate whitespace-nowrap" title={props.children}>
                  {props.children}
               </span>
            )}
         </div>
      </components.MultiValue>
   );
};

type SelectFieldProps = FormFieldProps & {
   clearable?: boolean;
   description?: string;
   disabled?: boolean;
   error?: string;
   multiple?: boolean;
   options: SelectOption[];
   placeholder?: string;
   required?: boolean;
   searchable?: boolean;
   startIcon?: IconType;
   OptionEndIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
   OptionStartIcon?: React.FC<React.SVGProps<SVGSVGElement>>;
   styles?: StylesConfig;
   title?: string;
   value?: string | string[];
   transition?: boolean;
   onChange?: (value: SelectOption | SelectOption[]) => void;
   onFocus?: (value: boolean) => void;
   optionIconWrapper?: string;
   menuListClassName?: string;
   renderMultiValueOutside?: boolean;
   maxVisibleMultiValues?: number;
   hideSelectedOptions?: boolean;
   menuPortalTarget?: HTMLElement | null;
   menuPosition?: 'absolute' | 'fixed';
   dropdownType?: string;
};

const SelectField: React.FC<SelectFieldProps> = ({
   clearable,
   description,
   disabled,
   error,
   multiple = false,
   options,
   placeholder,
   searchable,
   theme,
   title,
   value,
   startIcon,
   onChange,
   onFocus,
   styles,
   transition,
   OptionEndIcon,
   OptionStartIcon,
   optionIconWrapper,
   menuListClassName,
   renderMultiValueOutside = false,
   maxVisibleMultiValues,
   hideSelectedOptions,
   menuPortalTarget = typeof document !== 'undefined' ? document.body : null,
   menuPosition = 'fixed',
   dropdownType
}) => {
   const fieldId = useId()
   const descriptionId = description ? `${fieldId}-description` : undefined
   const errorId = error ? `${fieldId}-error` : undefined
   const ariaDescribedBy = [descriptionId, errorId].filter(Boolean).join(' ') || undefined

   const border = error ? 'border-twRed' : 'border-checkbox';

   const baseStyles: StylesConfig = useMemo(() => {
      const _styles: StylesConfig = {
         container: (base: CSSObjectWithLabel) => ({
            ...base,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
            maxWidth: '100%',
            cursor: 'pointer',
         }),
         control: (base: CSSObjectWithLabel) => ({
            ...base,
            border: 'none',
            borderRadius: '6px',
            boxShadow: 'none',
            height: maxVisibleMultiValues ? 'auto' : '100%',
            minHeight: '32px',
            cursor: 'pointer',
            alignItems: maxVisibleMultiValues ? 'flex-start' : 'stretch',
            ...(!maxVisibleMultiValues && {
               'div:first-child': {
                  maxHeight: '60px',
                  overflowY: 'auto',
                  alignSelf: 'center',
               },
            }),
         }),
         dropdownIndicator: (base: CSSObjectWithLabel) => ({
            ...base,
            padding: '6px 8px 6px 6px',
         }),
         indicatorSeparator: (base: CSSObjectWithLabel) => ({
            ...base,
            display: 'none',
         }),
         clearIndicator: (base: CSSObjectWithLabel) => ({
            ...base,
            borderRadius: '100%',
            backgroundColor: '#686868',
            padding: '3px',
            svg: {
               color: 'white !important',
            },
         }),
         input: (base: CSSObjectWithLabel) => ({
            ...base,
            minWidth: '0px',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
         }),
         menu: (base: CSSObjectWithLabel) => ({
            ...base,
            border: 'none',
            borderRadius: '6px',
            boxShadow: '1px 1px 20px 0px #0000001F',
            zIndex: 9999,
            minWidth: 'calc(100% + 3px)',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: '15px',
         }),
         menuPortal: (base: CSSObjectWithLabel) => ({
            ...base,
            border: 'none',
            boxShadow: 'none',
            zIndex: 9999,
         }),
         multiValue: (base: CSSObjectWithLabel) => ({
            ...base,
            backgroundColor: '#F7F7F7',
            borderRadius: '6px',
            fontSize: '14px',
            padding: '1px 2px',
         }),
         multiValueRemove: (base: CSSObjectWithLabel) => ({
            ...base,
            padding: '0px 5px',
            ':hover': {
               backgroundColor: '#E7E7E7',
            },
         }),
         noOptionsMessage: (base: CSSObjectWithLabel) => ({
            ...base,
            fontSize: '14px',
         }),
         option: (base: CSSObjectWithLabel, { isDisabled, isFocused }) => ({
            ...base,
            backgroundColor: isDisabled ? undefined : isFocused ? colorDefault?.colorLightPrimary : undefined,
            color: isDisabled ? undefined : isFocused ? colorDefault?.colorPrimary : undefined,
            fontSize: '14px',
            cursor: 'pointer',
            ':hover': {
               backgroundColor: colorDefault?.colorLightPrimary,
               color: colorDefault?.colorPrimary,
            },
         }),
         placeholder: (base: CSSObjectWithLabel) => ({
            ...base,
            color: '#686868',
            fontSize: '14px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            cursor: 'pointer',
            maxWidth: '80%',
         }),
         singleValue: (base: CSSObjectWithLabel) => ({
            ...base,
            flexWrap: 'nowrap',
            fontSize: '14px',
            maxWidth: '100%',
         }),
         valueContainer: (base: CSSObjectWithLabel) => ({
            ...base,
            display: 'flex',
            flexWrap: maxVisibleMultiValues ? 'wrap' : 'nowrap',
            alignItems: maxVisibleMultiValues ? 'flex-start' : 'center',
            fontSize: '14px',
            height: maxVisibleMultiValues ? 'auto' : '100%',
            padding: maxVisibleMultiValues ? '4px 8px' : '0 0 0 15px',
            overflow: 'hidden',
         }),
      };

      if (styles) {
         return merge(_styles, styles);
      }

      return _styles;
   }, [styles, maxVisibleMultiValues]);

   const selectRef = useRef<SelectInstance<any, any>>(null);
   const containerRef = useRef<HTMLDivElement>(null);
   const [menuOpen, setMenuOpen] = useState(false);

   useEffect(() => {
      if (!menuOpen || !containerRef.current) return;

      if (!dropdownType) {
         let el: HTMLElement | null = containerRef.current.parentElement;
         while (el && el !== document.body) {
            const { overflowY } = window.getComputedStyle(el);
            if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) break;
            el = el.parentElement;
         }
         const target = el ?? document.body;
         const prev = target.style.overflow;
         target.style.overflow = 'hidden';

         return () => {
            target.style.overflow = prev;
         };
      }
   }, [menuOpen]);

   useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
         const menuEl = document.querySelector('[role="listbox"]');

         if (containerRef.current && !containerRef.current.contains(event.target as Node) && !(menuEl && menuEl.contains(event.target as Node))) {
            setMenuOpen(false);
         }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === 'Escape') {
            setMenuOpen(false);
         }
      };

      document.addEventListener('mouseup', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      return () => {
         document.removeEventListener('mouseup', handleClickOutside);
         document.removeEventListener('keydown', handleKeyDown);
      };
   }, []);

   const selectedOptions = useMemo(() => {
      return (options ?? []).filter(option => new Set(Array.isArray(value) ? value : [value]).has(option.value));
   }, [options, value]);

   const filteredOptions = useMemo(() => {
      if (!multiple || !hideSelectedOptions) return options ?? [];
      const selectedValues = new Set(Array.isArray(value) ? value : []);
      const unselected = options.filter(o => !selectedValues.has(o.value));
      if (unselected.length === 0) return [];
      return [{ label: 'Select All', value: '__select_all__' } as SelectOption, ...unselected];
   }, [options, value, multiple, hideSelectedOptions]);

   const tagsContainerRef = useRef<HTMLDivElement>(null);
   const [isOverflowing, setIsOverflowing] = useState(false);
   const [visibleCount, setVisibleCount] = useState(selectedOptions.length);
   const [isModalOpen, setIsModalOpen] = useState(false);

   useLayoutEffect(() => {
      if (!renderMultiValueOutside || selectedOptions.length === 0 || !tagsContainerRef.current) return;

      const container = tagsContainerRef.current;
      const children = Array.from(container.children) as HTMLElement[];

      // Reset for measurement
      setIsOverflowing(false);
      setVisibleCount(selectedOptions.length);

      if (children.length === 0) return;

      // Calculate the Y offset of the first element to determine row positions
      const firstY = children[0].offsetTop;
      let currentLine = 1;
      let lastY = firstY;
      let cutOffIndex = children.length;

      for (let i = 0; i < children.length; i++) {
         const child = children[i];
         // Ignore the "More" button if it's currently rendered during a re-calc
         if (child.id === 'more-btn-stub') continue;

         if (child.offsetTop > lastY) {
            currentLine++;
            lastY = child.offsetTop;
         }

         if (currentLine > 2) {
            cutOffIndex = i;
            break;
         }
      }

      if (cutOffIndex < selectedOptions.length) {
         setIsOverflowing(true);
         setVisibleCount(cutOffIndex);
      } else {
         setIsOverflowing(false);
         setVisibleCount(selectedOptions.length);
      }
   }, [selectedOptions, renderMultiValueOutside, containerRef.current?.offsetWidth]);

   return (
      <>
         <div className={theme?.container}>
            {/* Title */}
            {!transition && title !== undefined && <p className="m-0 font-semibold text-sm mb-2.5">{title}</p>}
            {/* Main input */}
            <div
               ref={containerRef}
               onMouseDown={e => {
                  const target = e.target as HTMLElement;
                  if (target.closest('.select-clear-indicator')) return;

                  e.preventDefault();
                  setMenuOpen(!menuOpen);
               }}
               className={twMerge(`w-full flex items-center border rounded-md space-x-2.5 relative`, border, theme?.border)}
            >
               {transition && (
                  <div className={`text-form__label absolute block bg-white px-2 transition ml-3.75 ${value ? '-top-4' : ''}`}>{title}</div>
               )}
               <Select
                  ref={selectRef}
                  aria-invalid={Boolean(error)}
                  aria-errormessage={error ? errorId : undefined}
                  className={theme?.input}
                  components={{
                     ClearIndicator,
                     DropdownIndicator,
                     MultiValueRemove,
                     SingleValue,
                     MultiValue,
                     ...(startIcon ? { Control } : {}),
                     ...(multiple ? { Option: (props: any) => Option({ ...props, OptionStartIcon, OptionEndIcon, optionIconWrapper }) } : {}),
                     ...(multiple ? { MenuList: (props: any) => MenuList({ ...props, menuListClassName }) } : {}),
                     ...(maxVisibleMultiValues ? { ValueContainer: MaxRowsValueContainer } : {}),
                  }}
                  isDisabled={disabled}
                  isClearable={clearable}
                  isMulti={multiple}
                  isSearchable={searchable}
                  controlShouldRenderValue={!(multiple && renderMultiValueOutside)}
                  {...(maxVisibleMultiValues ? { maxRows: maxVisibleMultiValues, onShowMoreClick: () => setIsModalOpen(true) } as any : {})}
                  filterOption={(option, inputValue) => {
                     const search = inputValue.toLowerCase();
                     const text = option.label;
                     return text.toLowerCase().includes(search);
                  }}
                  menuPortalTarget={menuPortalTarget}
                  menuPosition={menuPosition}
                  menuIsOpen={menuOpen}
                  onMenuOpen={() => {
                     if (onFocus) {
                        onFocus(true);
                     }
                     return setMenuOpen(true);
                  }}
                  onMenuClose={() => {
                     if (onFocus) {
                        onFocus(false);
                     }
                     !multiple && setMenuOpen(false);
                  }}
                  options={filteredOptions}
                  placeholder={<span title={placeholder || 'Select an option'}>{placeholder || 'Select an option'}</span>}
                  styles={baseStyles}
                  value={selectedOptions}
                  {...((ariaDescribedBy || (error ? errorId : undefined)) && ({ "aria-describedby": ariaDescribedBy || errorId } as any))}
                  onChange={(val: unknown) => {
                     if (!multiple) { onChange?.(val as SelectOption); return; }
                     const list = val as SelectOption[];
                     if (list.some(o => o.value === '__select_all__')) {
                        const selectedVals = new Set(Array.isArray(value) ? value : []);
                        const unselected = options.filter(o => !selectedVals.has(o.value));
                        onChange?.([...selectedOptions, ...unselected]);
                     } else {
                        onChange?.(list);
                     }
                  }}
                  hideSelectedOptions={!multiple}
               />
            </div>

            {/* External Multi-Value Renderer */}
            {multiple && renderMultiValueOutside && selectedOptions.length > 0 && (
               <div ref={tagsContainerRef} className="flex flex-wrap gap-2.5 mt-2.5 overflow-hidden">
                  {selectedOptions.slice(0, isOverflowing ? Math.max(0, visibleCount - 1) : selectedOptions.length).map(option => (
                     <div key={option.value} className="flex items-center bg-lightGray rounded text-sm py-1.75 px-1.5">
                        <div className="flex items-center gap-2 justify-between">
                           {option.HtmlInputLabel ? (
                              <option.HtmlInputLabel />
                           ) : (
                              <span className="truncate whitespace-nowrap" title={option.label}>
                                 {option.label}
                              </span>
                           )}
                        </div>
                        <button
                           type="button"
                           className="ml-1 px-1.25 py-0.5 hover:bg-gray-200 rounded select-clear-indicator flex items-center justify-center"
                           onClick={e => {
                              e.stopPropagation();
                              onChange?.(selectedOptions.filter(o => o.value !== option.value));
                           }}
                        >
                           <HiOutlineX size={10} className="text-black pointer-events-none" />
                        </button>
                     </div>
                  ))}

                  {/* The More Button */}
                  {isOverflowing && (
                     <button
                        id="more-btn-stub"
                        type="button"
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium rounded text-sm py-1.75 px-2.5 transition-colors"
                     >
                        +{selectedOptions.length - Math.max(0, visibleCount - 1)} more...
                     </button>
                  )}
               </div>
            )}

            {/* Error text */}
            {error !== undefined && <p id={errorId} role="alert" className="m-0 text-twRed mt-2.5 text-xs">{error}</p>}
            {/* Description / Help text */}
            {description !== undefined && <div id={descriptionId} className="m-0 mt-2.5 text-twGray text-sm leading-4.5">{description}</div>}
         </div>
         <ShowMoreMultipleItemsModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            options={selectedOptions}
            onRemove={valueToRemove => {
               onChange?.(selectedOptions.filter(o => o.value !== valueToRemove));
            }}
            title={title}
         />
      </>
   );
};

export type { SelectOption };
export default SelectField;
